'use client';

import { Badge, Skeleton } from '@common/components';
import { SvgMedicalCalendar } from '@common/icon';
import { useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';

import { ListItem } from './ListItem';

interface MedicalResourceSectionProps {
  emergencyRoomId: number;
  babyRoomTotalCount?: string;
  roomTotalCount?: string;
}

const EMPTY_STRING = '해당 없음';

export const MedicalResourceSection = ({
  emergencyRoomId,
  babyRoomTotalCount,
  roomTotalCount,
}: MedicalResourceSectionProps) => {
  const { data, isLoading } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/emergency-room/{emergencyRoomId}/live'
    ]({
      path: {
        emergencyRoomId,
      },
    }),
    refetchInterval: 60_000,
    select: ({ context }) => ({
      roomCount: context?.hv36,
      babyRoomCount: context?.hv37,
      ambulanceUsable: context?.hvamyn,
      ctUsable: context?.hvctayn,
      mriUsable: context?.hvmriayn,
      doctorName: context?.hvdnm,
    }),
  });

  const { babyRoomCount, roomCount } = data ?? {};

  const medicalSupportList = [
    ['전문의 기본 정보', '전문의', data?.doctorName],
    ['CT 가용 여부', 'CT', data?.ctUsable],
    ['MRI 가용 여부', 'MRI', data?.mriUsable],
    ['구급차 운영 여부', '구급차', data?.ambulanceUsable],
  ] as const;

  const emptyMedicalSupportList: string[] = [];
  const filledMedicalSupportList = medicalSupportList.filter(
    ([, shortTitle, value]) => {
      const isFilled = value !== null;

      if (!isFilled) emptyMedicalSupportList.push(shortTitle);

      return isFilled;
    },
  );

  const hasMedicalSupportInfo = !!filledMedicalSupportList.length;

  const emptyMedicalSupportText = emptyMedicalSupportList.length
    ? `이 병원은 ${emptyMedicalSupportList.join(', ')}와 관련된 정보를 제공하지 않고 있습니다. `
    : null;

  const hasRoomCount = roomCount && roomTotalCount;
  const hasBabyRoomCount = babyRoomCount && babyRoomTotalCount;

  const noRoomCount = !hasRoomCount && !hasBabyRoomCount;

  return (
    <section className="flex flex-col">
      <h3 className="mb-12 size-fit gap-8 font-500 text-Gray-900 flex-center typo-body4">
        <SvgMedicalCalendar className="size-16 text-G-500" />
        의료자원
      </h3>
      {isLoading ? (
        <div className="flex flex-col gap-8">
          <Skeleton className="h-21 w-120" />
          <Skeleton className="h-21 w-170" />
          <Skeleton className="h-21 w-130" />
          <Skeleton className="h-21 w-150" />
        </div>
      ) : (
        <ul className="flex flex-col gap-8">
          {hasMedicalSupportInfo ? (
            <>
              {filledMedicalSupportList.map(([title, shortTitle, value]) => {
                const isAvailable = value === 'Y';

                return (
                  <ListItem key={shortTitle}>
                    {title} :
                    <Badge
                      className={isAvailable ? 'text-G-600' : 'text-R-300'}
                    >
                      {isAvailable ? '가능' : '불가능'}
                    </Badge>
                  </ListItem>
                );
              })}
              {emptyMedicalSupportText ? (
                <li className="ml-8 flex gap-7 text-Gray-800 typo-body4">
                  <div className="before:content-['·']" />
                  {emptyMedicalSupportText}
                </li>
              ) : null}
            </>
          ) : (
            <li className="ml-8 flex gap-7 text-Gray-800 typo-body4">
              <div className="before:content-['·']" />이 병원은 의료 자원과
              관련된 정보를 제공하지 않습니다.
            </li>
          )}
        </ul>
      )}
      <h3 className="mb-12 mt-32 size-fit gap-8 font-500 text-Gray-900 flex-center typo-body4">
        <SvgMedicalCalendar className="size-16 text-G-500" />
        응급실 가용여부
      </h3>
      {isLoading ? (
        <div className="flex flex-col gap-8">
          <Skeleton className="h-21 w-120" />
          <Skeleton className="h-21 w-170" />
        </div>
      ) : noRoomCount ? (
        <ul>
          <ListItem>
            이 병원은 응급실 병상수와 관련된 정보를 제공하지 않습니다.
          </ListItem>
        </ul>
      ) : (
        <ul>
          <ListItem>
            응급실 일반 가용 병상수 / 기준 병상수 :{' '}
            {hasRoomCount ? (
              <>
                <span className="text-G-500">{roomCount}</span>/{roomTotalCount}
              </>
            ) : (
              EMPTY_STRING
            )}
          </ListItem>
          <ListItem>
            응급실 소아 가용 병상수 / 기준 병상수 :{' '}
            {hasBabyRoomCount ? (
              <>
                <span className="text-G-500">{babyRoomCount}</span>/
                <span>{babyRoomTotalCount}</span>
              </>
            ) : (
              EMPTY_STRING
            )}
          </ListItem>
        </ul>
      )}
    </section>
  );
};
